// ============================================================================
// LeadDrawer — full detail for one discovered lead in the sourcing inbox.
//
// Mirrors prospect-drawer.tsx: right-side overlay panel, overlayClickClose,
// sticky header + footer. Turns the terse raw_json.detail string (PPP loan /
// federal-contract facts) into plain English, shows location + source +
// contact, and — once enriched — the deeper qualification fields. Footer
// actions: Promote to pipeline / Dig deeper (queue enrichment) / Discard.
// ============================================================================

import { useMemo, useState } from 'react';
import {
  Ban,
  Building2,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Rocket,
  Sparkles,
  X,
} from 'lucide-react';
import type { DiscoveredLead, SourcingSource } from './hooks/use-sourcing-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { AlreadyOnFileNotice, alreadyOnFileFrom, type AlreadyOnFileError } from '../subcontractors/already-on-file';

interface Props {
  lead: DiscoveredLead;
  sources: SourcingSource[];
  busy: boolean;
  onClose: () => void;
  onPromote: (lead: DiscoveredLead, addAnyway?: boolean) => Promise<void> | void;
  onDiscard: (leadId: string) => Promise<void> | void;
  onDigDeeper: (leadId: string) => Promise<void> | void;
}

function money(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return `$${raw}`;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/** Parse the terse `raw_json.detail` string into readable sentences. */
function summarizeDetail(lead: DiscoveredLead): string[] {
  const raw = lead.raw_json && typeof lead.raw_json.detail === 'string' ? lead.raw_json.detail : '';
  if (!raw) return [];
  const out: string[] = [];

  const ppp = raw.match(/PPP\s+\$([\d.]+)/i);
  if (ppp) out.push(`Received a PPP loan of ${money(ppp[1])}.`);

  const jobs = raw.match(/jobs\s+(\d+)/i);
  if (jobs) out.push(`Reported ${jobs[1]} job${jobs[1] === '1' ? '' : 's'} retained.`);

  const type = raw.match(/type\s+([^;]+)/i);
  if (type) out.push(`Registered as ${type[1].trim().replace(/\s+/g, ' ')}.`);

  const approved = raw.match(/approved\s+([\d/]+)/i);
  if (approved) out.push(`Loan approved ${approved[1]}.`);

  const award = raw.match(/award\s+([^;]+)/i);
  if (award) out.push(`Federal contract award ${award[1].trim()}.`);

  const agency = raw.match(/agency\s+([^;]+)/i);
  if (agency) out.push(`Contracting agency: ${agency[1].trim()}.`);

  const obligated = raw.match(/obligated\s+\$([\d.]+)/i);
  if (obligated) out.push(`Obligated amount ${money(obligated[1])}.`);

  const pop = raw.match(/PoP\s+([^;]+)/i);
  if (pop) out.push(`Place of performance: ${pop[1].trim()}.`);

  return out;
}

function yn(v: boolean | null): string {
  return v === null ? '—' : v ? 'Yes' : 'No';
}

const ENRICHMENT_LABELS: Record<DiscoveredLead['enrichment_status'], string> = {
  none: 'Not enriched',
  queued: 'Queued for enrichment',
  enriched: 'Enriched',
  no_match: 'No match found',
  error: 'Enrichment error',
};

export function LeadDrawer({
  lead,
  sources,
  busy,
  onClose,
  onPromote,
  onDiscard,
  onDigDeeper,
}: Props) {
  const [action, setAction] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [onFile, setOnFile] = useState<AlreadyOnFileError | null>(null);

  const summary = useMemo(() => summarizeDetail(lead), [lead]);

  const sourceName = lead.source_id
    ? (sources.find((s) => s.source_id === lead.source_id)?.name ?? lead.source_id)
    : null;
  const datasets =
    lead.raw_json && Array.isArray(lead.raw_json.datasets)
      ? (lead.raw_json.datasets as unknown[]).map((d) => String(d))
      : [];

  const rj = lead.raw_json ?? {};
  const state = typeof rj.state === 'string' ? rj.state : null;
  const zip = typeof rj.zip === 'string' ? rj.zip : null;
  const cityLine = [lead.city_name, state].filter(Boolean).join(', ');
  const phone = lead.phone ?? lead.enriched_phone;
  const email = lead.email ?? lead.enriched_email;
  const isEnriched = lead.enrichment_status === 'enriched';
  const digDisabled =
    busy || lead.enrichment_status === 'queued' || lead.enrichment_status === 'enriched';
  const digLabel =
    lead.enrichment_status === 'queued'
      ? 'Queued'
      : lead.enrichment_status === 'enriched'
        ? 'Enriched'
        : 'Dig deeper';

  const run = async (key: string, fn: () => Promise<void> | void) => {
    setAction(key);
    setErr(null);
    setOnFile(null);
    try {
      await fn();
    } catch (e) {
      const found = alreadyOnFileFrom(e);
      if (found) setOnFile(found);
      else setErr(errorMessage(e, 'Action failed.'));
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-tcs-scrim/60" {...overlayClickClose(onClose)}>
      <div
        className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-tcs-bg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="sticky top-0 z-10 border-b border-tcs-border bg-tcs-bg p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
                {lead.lead_id} · {ENRICHMENT_LABELS[lead.enrichment_status]}
              </div>
              <h2 className="truncate text-lg font-semibold text-tcs-text">{lead.business_name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-tcs-text-muted">
                {lead.market && <span>{lead.market}</span>}
                {lead.entity_type && (
                  <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                    {lead.entity_type.replace(/\s+/g, ' ')}
                  </span>
                )}
                {lead.ppp_jobs !== null && (
                  <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-semibold">
                    {lead.ppp_jobs} jobs
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-tcs-text-muted hover:bg-tcs-bg-muted"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-5 p-4">
          {err && (
            <div className="rounded-md border border-st-danger-border bg-st-danger-bg p-2 text-xs text-st-danger-fg">
              {err}
            </div>
          )}
          {onFile && (
            <AlreadyOnFileNotice
              found={onFile}
              busy={busy || action === 'promote'}
              onOpen={onClose}
              onAddAnyway={() => run('promote', () => onPromote(lead, true))}
            />
          )}

          {/* what we know (parsed from the source record) */}
          {summary.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                What the record shows
              </h3>
              <ul className="space-y-1 text-sm text-tcs-text">
                {summary.map((line, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-tcs-text-muted">·</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* location + source */}
          <section>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              Location &amp; source
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-tcs-text-muted">Location</dt>
              <dd className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-tcs-text-muted" />
                {[cityLine, zip].filter(Boolean).join(' ') || '—'}
              </dd>
              <dt className="text-tcs-text-muted">Market</dt>
              <dd>{lead.market ?? '—'}</dd>
              <dt className="text-tcs-text-muted">Owner</dt>
              <dd>{lead.owner_name ?? '—'}</dd>
              <dt className="text-tcs-text-muted">Source</dt>
              <dd>{sourceName ?? '—'}</dd>
              {datasets.length > 0 && (
                <>
                  <dt className="text-tcs-text-muted">Datasets</dt>
                  <dd>{datasets.join(', ')}</dd>
                </>
              )}
            </dl>
          </section>

          {/* contact */}
          <section>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              Contact
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-tcs-text-muted">Phone</dt>
              <dd className="flex items-center gap-1">
                {phone ? (
                  <>
                    <Phone className="h-3.5 w-3.5 text-tcs-text-muted" />
                    {phone}
                    {!lead.phone && lead.enriched_phone && (
                      <span className="text-[10px] text-st-ok-fg">(enriched)</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </dd>
              <dt className="text-tcs-text-muted">Email</dt>
              <dd className="flex items-center gap-1 truncate">
                {email ? (
                  <>
                    <Mail className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
                    <span className="truncate">{email}</span>
                    {!lead.email && lead.enriched_email && (
                      <span className="text-[10px] text-st-ok-fg">(enriched)</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </dd>
              <dt className="text-tcs-text-muted">Website</dt>
              <dd className="truncate">
                {lead.website ? (
                  <a
                    href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-st-info-fg hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="truncate">{lead.website}</span>
                  </a>
                ) : (
                  '—'
                )}
              </dd>
            </dl>
          </section>

          {/* enrichment findings */}
          {isEnriched && (
            <section className="rounded-lg border border-tcs-border p-3">
              <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                <Sparkles className="h-3.5 w-3.5" /> Enrichment findings
              </h3>
              {lead.enrichment_summary && (
                <p className="mb-2 whitespace-pre-wrap text-sm text-tcs-text">
                  {lead.enrichment_summary}
                </p>
              )}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-tcs-text-muted">Years in business</dt>
                <dd>{lead.years_in_business ?? '—'}</dd>
                <dt className="text-tcs-text-muted">Has insurance</dt>
                <dd>{yn(lead.has_insurance)}</dd>
                <dt className="text-tcs-text-muted">Medical experience</dt>
                <dd>{yn(lead.medical_experience)}</dd>
                <dt className="text-tcs-text-muted">Subcontracts</dt>
                <dd>{yn(lead.subcontracts)}</dd>
                {lead.services && lead.services.length > 0 && (
                  <>
                    <dt className="text-tcs-text-muted">Services</dt>
                    <dd className="flex flex-wrap gap-1">
                      {lead.services.map((svc) => (
                        <span
                          key={svc}
                          className="rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] text-tcs-text"
                        >
                          {svc}
                        </span>
                      ))}
                    </dd>
                  </>
                )}
              </dl>
              {lead.enrichment_source && (
                <p className="mt-2 text-[10px] text-tcs-text-muted">
                  Source: {lead.enrichment_source}
                  {lead.enriched_at && ` · ${new Date(lead.enriched_at).toLocaleDateString()}`}
                </p>
              )}
            </section>
          )}
        </div>

        {/* footer actions */}
        <div className="sticky bottom-0 mt-auto flex flex-wrap items-center gap-2 border-t border-tcs-border bg-tcs-bg p-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => run('promote', () => onPromote(lead))}
            className="inline-flex items-center gap-1.5 rounded-md bg-tcs-inverse px-3 py-2 text-sm font-semibold text-tcs-inverse-ink hover:opacity-90 disabled:opacity-40"
          >
            {action === 'promote' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="h-4 w-4" />
            )}
            Promote to pipeline
          </button>
          <button
            type="button"
            disabled={digDisabled}
            onClick={() => run('dig', () => onDigDeeper(lead.lead_id))}
            title={
              isEnriched
                ? 'Already enriched.'
                : lead.enrichment_status === 'queued'
                  ? 'Queued for enrichment.'
                  : 'Queue this lead for enrichment (contact, insurance, services).'
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border px-3 py-2 text-sm font-medium hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            {action === 'dig' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            {digLabel}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => run('discard', () => onDiscard(lead.lead_id))}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-tcs-text-muted hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            <Ban className="h-4 w-4" /> Discard
          </button>
        </div>
      </div>
    </div>
  );
}
